/**
 * The decisions SearchListing makes about its showcase rows, as plain functions:
 * which listing ids are already on screen, what props a tag or group row pages
 * with, which group seeds the tag rows, and when the showcase view offers a
 * next page. Kept out of the component so each can be tested without rendering it.
 */
import { Cats } from '../../generated/static-categories';
import type { ListingSort } from '../../utils/listing-sort';

/** One appended tag-showcase row: a tag, its heading, and its seed listings. */
/** `categoryGroupId`: the group the server held the row to; its later pages stay in it. */
export type TagShowcaseRow = {
  tag: string;
  title?: string;
  listings: Record<string, unknown>[];
  categoryGroupId?: number;
  brand?: string;
};

/** A brand row's See All: the group's browse page filtered to the brand, or search when the row has no group. */
export function brandSeeAllUrl(brand: string, categoryGroupId?: number): string {
  const group = categoryGroupId !== undefined ? Cats[categoryGroupId] : undefined;
  const query = `brand=${encodeURIComponent(brand)}`;
  return group ? `/browse/${group.slug}?${query}` : `/search?${query}`;
}

/**
 * Best-effort list of listing ids already on screen, so appended tag rows do not
 * repeat them. An id that is not a number (a row without one) is skipped.
 */
export function shownListingIds(rows: ReadonlyArray<{ listings?: readonly unknown[] }>, list: readonly unknown[]): number[] {
  const ids: number[] = [];
  const push = (l: unknown) => {
    const n = Number((l as { id?: unknown } | null)?.id);
    if (Number.isFinite(n)) ids.push(n);
  };
  rows.forEach((r) => r.listings?.forEach(push));
  list.forEach(push);
  return ids;
}

/**
 * The group a category page's tag rows are seeded from: the page's own group,
 * else the search's. Anything that is not a number seeds from every group.
 */
export function seedGroupId(pageGroupId: number | undefined, searchGroupId: unknown): number | undefined {
  const id = pageGroupId ?? searchGroupId;
  return typeof id === 'number' ? id : undefined;
}

/** The search a category or group row pages with. On home a row is a GROUP; everywhere else a single category. */
export function groupRowSearchParams(isHome: boolean, id: number, rowSort: ListingSort | undefined) {
  return {
    // Filing a category id under categoryGroupId made its "Not Interested"
    // name a group that does not exist, so ranking could never find it.
    ...(isHome ? { categoryGroupId: id } : { categoryId: id }),
    hasPhoto: true,
    // The row's next pages come in the same order as its first.
    ...(rowSort ? { sortBy: rowSort } : {}),
  };
}

/** A tag (or brand) row's heading, paging search, See All link, and whether it tops up forever. */
export function tagRowProps(
  row: { tag: string; title?: string; brand?: string; categoryGroupId?: number },
  rowSort: ListingSort | undefined,
) {
  return {
    title: row.title || row.tag.charAt(0).toUpperCase() + row.tag.slice(1),
    searchParams: {
      // A brand row pages by the brand field; its `tag` is only the row key (brand:nike).
      ...(row.brand ? { brand: [row.brand] } : { tags: [row.tag] }),
      hasPhoto: true,
      ...(row.categoryGroupId !== undefined && { categoryGroupId: row.categoryGroupId }),
      ...(rowSort ? { sortBy: rowSort } : {}),
    },
    seeAllUrl: row.brand ? brandSeeAllUrl(row.brand, row.categoryGroupId) : undefined,
    // A brand row ends at the brand's last listing: the tag-graph top-up would fill a Nike row with other brands.
    recommendForever: !row.brand,
  };
}

/** Appended tag rows, less any tag home already ranked onto its first screen. */
export function unrankedTagRows<R extends { tag: string }>(
  rows: readonly R[],
  homeShowcaseView: boolean,
  rankedTagRows: ReadonlyArray<{ tag: string }>,
): R[] {
  if (!homeShowcaseView) return [...rows];
  return rows.filter((row) => !rankedTagRows.some((t) => t.tag === row.tag));
}

/**
 * Whether the showcase view offers a next page. Home's rows page with
 * useHomeShowcases, so it waits for real rows; elsewhere the grid list or the
 * tag-row continuation is what pages. Never while a category page's first rows
 * are on their way.
 */
export function showcaseLoadMoreVisible(s: {
  homeShowcaseView: boolean;
  homeRowCount: number;
  tagRowCount: number;
  tagShowcaseMode: boolean;
  listCount: number;
  categoryShowcasesPending: boolean;
}): boolean {
  if (s.categoryShowcasesPending) return false;
  if (s.homeShowcaseView) return s.homeRowCount > 0 || s.tagRowCount > 0;
  return s.listCount > 0 || s.tagShowcaseMode;
}
